import React, { useState } from 'react'
import { Box, Typography, Grid, DialogTitle, IconButton, Stack, Theme, styled, useTheme } from '@mui/material'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { ShowDialogProps } from 'src/show-dialog'
import { useTranslation } from 'react-i18next'
import { useGetFormSchemaReports } from 'src/api/form-schema'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import NoChecklist from 'src/assets/svg/no-items-common-image.svg'
import { FlexSpinner } from 'src/components/Spinner'
import ChecklistAccordionItem from 'src/components/checklists/ChecklistAccordionItem'
import { formSchemaReportTypes, SchemaType } from 'src/generated-types/form-schema'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { Flex } from 'src/styles/flexComponent'
import { Close } from '@mui/icons-material'

interface ActivityLogReportsDialogProps extends ShowDialogProps {
  customerId: EntityId
  activityLogId: EntityId
  localityId?: EntityId
  serviceStationId?: EntityId
  storageId?: EntityId
}

export const ActivityLogReportsDialog: React.FC<ActivityLogReportsDialogProps> = ({
  pop,
  customerId,
  activityLogId,
  localityId,
  serviceStationId,
  storageId,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['checklist', 'common'])
  const { data, isLoading } = useGetFormSchemaReports(customerId, {
    type: formSchemaReportTypes.MainComponent,
    activityLogId: activityLogId as EntityId,
    schemaType: SchemaType.Checklist,
  })
  const [expandedReportId, setExpandedReportId] = useState<number | undefined>(undefined)

  const rows = React.useMemo(() => {
    const filled: any[] = []
    data?.forEach(schema => {
      if (schema.formSchemaReports && schema.formSchemaReports.length > 0) filled.push(...schema.formSchemaReports)
    })
    return filled
  }, [data])

  const handleToggle = (reportId: number | undefined) => {
    setExpandedReportId(reportId)
  }

  return (
    <GenericDialogLayout
      pop={pop}
      sx={{ padding: 3 }}
      renderCustomHeader={pop => (
        <HeaderContainer theme={theme}>
          <StyledDialogTitle>
            <HeaderStack>
              <HeaderTitle theme={theme}>{t('completed-checklist-history', { ns: 'checklist' })}</HeaderTitle>
              <Flex.Row alignItems="center" gap={1}>
                {pop && (
                  <CloseIconButton aria-label="close" onClick={pop} theme={theme}>
                    <Close />
                  </CloseIconButton>
                )}
              </Flex.Row>
            </HeaderStack>
          </StyledDialogTitle>
        </HeaderContainer>
      )}
    >
      {isLoading ? (
        <FlexSpinner />
      ) : rows.length === 0 ? (
        <Box>
          <EmptyStatusCard
            image={NoChecklist}
            header={t('no-completed-checklist-history', { ns: 'checklist' })}
            headerFontSize="18px"
            contentFontSize="14px"
            height="200px"
            imageWidth="120px"
          />
        </Box>
      ) : (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {rows.map(report => (
                <ChecklistAccordionItem
                  key={report.id}
                  report={report}
                  expanded={expandedReportId === report.id}
                  onToggle={handleToggle}
                  disabled={true}
                  hideDelete={true}
                  createPermissions={[]}
                  deletePermissions={[]}
                  permission={0}
                  customerId={customerId}
                  localityId={localityId}
                  serviceStationId={serviceStationId}
                  storageId={storageId}
                />
              ))}
            </Grid>
          </Grid>
        </Box>
      )}
    </GenericDialogLayout>
  )
}

export default ActivityLogReportsDialog

const HeaderContainer = styled(Box)<{ theme: Theme }>(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  height: '50px',
}))

const StyledDialogTitle = styled(DialogTitle)({
  padding: '12px 16px 0 16px',
})

const HeaderStack = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '-5px',
})

const HeaderTitle = styled(Typography)<{ theme: Theme }>(({ theme }) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 700,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const CloseIconButton = styled(IconButton)<{ theme: Theme }>(({ theme }) => ({
  '& svg': {
    color: getThemeColor(theme, MUIThemeColors.secondaryMain),
    fontSize: '18px',
  },
}))
